import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { ImageView, ImageViewOpts } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import Scroll from '../components/shared/Scroll';
import { getApplication } from 'src/lib/utils';
import StateObserver from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { isDecorationTutorialInProgress } from './helpers';
import TutorialHand from '../components/shared/TutorialHand';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = () => void;
type RoomID = 'living' | 'toilet' | 'bathroom' | 'dining';
type Room = { id: RoomID; name: string; locked: boolean };
type RoomPair = [Room, Room];

/**
 * Show available rooms for decoration
 */
export default class PopupSelectDecorationRoom extends PopupBasic {
  private buttonBack: ButtonScaleViewWithText;
  private close: CloseHandler;
  private list: Scroll<RoomPair>;
  private hand: TutorialHand;

  /**
   * Constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      width: 563,
      height: 872,
      darkerBg: true,
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.SELECT_ROOM_POPUP._,
      },
      titleIcon: {
        width: 41,
        height: 56,
        image: 'assets/ui/decoration/icons/decoration_icon.png',
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.title.updateOpts({ width: 600 });
    this.message.updateOpts({ y: 88, size: 23 });

    // Rooms list
    this.list = new Scroll<RoomPair>({
      superview: this.box,
      createItem: (...args) => this.createPair(...args),
      showBg: true,
      horizontal: false,
      firstItemOffset: 5,
      margin: 10,
      rect: {
        x: (this.box.style.width - 516) * 0.5,
        y: 140,
        width: 516,
        height: 585,
      },
    });

    // Close button
    this.buttonBack = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 40,
      font: bitmapFonts('Body'),
      x: (563 - 228) * 0.5,
      y: 752,
      width: 228,
      height: 94.7,
      localeText: () => i18n('decoration.selectRoomPopup.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.SELECT_ROOM_POPUP.BACK,
      },
    });

    // Tutorial hand
    this.hand = new TutorialHand({});
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/decoration/icons/decoration_icon.png',
      'assets/ui/decoration/buttons/roomselect_button_inactive.png',
      'assets/ui/decoration/buttons/roomselect_button_up.png',
      'assets/ui/decoration/buttons/roomselect_button_down.png',
      'assets/ui/decoration/frames/roomselect_button_disabled_overlay.png',
      'assets/ui/shared/icons/footer_locked.png',
      'assets/ui/decoration/icons/living_icon.png',
      'assets/ui/decoration/icons/toilet_icon.png',
      'assets/ui/decoration/icons/bathroom_icon.png',
      'assets/ui/decoration/icons/dining_icon.png',
    ]);
  }

  /**
   * On dialog init
   */
  public init() {
    super.init({
      title: i18n('decoration.selectRoomPopup.title'),
      message: i18n('decoration.selectRoomPopup.message'),
    });

    const data: RoomPair[] = [
      [
        { id: 'living', name: i18n('decoration.rooms.living'), locked: false },
        {
          id: 'toilet',
          name: i18n('decoration.rooms.toilet'),
          locked: false,
        },
      ],
      [
        {
          id: 'bathroom',
          name: i18n('decoration.rooms.bath'),
          locked: true,
        },
        {
          id: 'dining',
          name: i18n('decoration.rooms.dining'),
          locked: true,
        },
      ],
    ];

    // Set data
    this.list.setItems(data);

    // Tutorial show
    this.tutorialShow();
  }

  /**
   * Create one row view
   */
  private createPair(superview: View, _: number, pair: RoomPair) {
    const row = new View({
      superview,
      width: superview.style.width,
      height: 266,
    });

    this.createRoomView(row, { x: 15 }, pair[0]);
    this.createRoomView(row, { x: 265 }, pair[1]);

    return row;
  }

  /**
   * Create room view
   */
  private createRoomView(superview: View, opts: ViewOpts, data: Room) {
    const cell = new ButtonScaleView({
      superview: superview,
      ...opts,
      image: data.locked
        ? 'assets/ui/decoration/buttons/roomselect_button_inactive.png'
        : 'assets/ui/decoration/buttons/roomselect_button_up.png',
      imagePressed: 'assets/ui/decoration/buttons/roomselect_button_down.png',
      imageDisabled:
        'assets/ui/decoration/buttons/roomselect_button_inactive.png',
      pressedOffsetY: 4,
      width: 238,
      height: 271,
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.SELECT_ROOM_POPUP.SELECTED,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.openRoom(data.id);
      },
    });

    new ImageView({
      superview: cell,
      x: cell.style.width / 2,
      y: 98,
      centerOnOrigin: true,
      ...this.getRoomIcon(data.id),
    });

    new LangBitmapFontTextView({
      superview: cell,
      x: (234 - 200) * 0.5,
      y: 200,
      width: 200,
      height: 25,
      align: 'center',
      verticalAlign: 'center',
      size: 22,
      color: '#BF8147',
      localeText: () => data.name,
      font: bitmapFonts('Body'),
    });

    if (data.locked) {
      new ImageView({
        superview: cell,
        width: 238,
        height: 271,
        image:
          'assets/ui/decoration/frames/roomselect_button_disabled_overlay.png',
      });

      new ImageView({
        superview: cell,
        x: 190,
        y: -9,
        width: 50,
        height: 59,
        image: 'assets/ui/shared/icons/footer_locked.png',
      });
    }

    return cell;
  }

  /**
   * Open room
   */
  private async openRoom(room: RoomID) {
    // Hide hand
    this.tutorialHide();

    switch (room) {
      case 'living':
      case 'toilet':
        this.opts.close();
        return getApplication().screenManager.showScreen('decoration', {
          room,
        });
      case 'dining':
      case 'bathroom':
        return openPopupPromise('popupFeatureDisabled', {
          feature: 'decorationRoomDisabled',
        });
    }
  }

  /**
   * Get room icon
   */
  private getRoomIcon(
    room: 'living' | 'toilet' | 'bathroom' | 'dining',
  ): ImageViewOpts {
    switch (room) {
      case 'living':
        return {
          width: 130,
          height: 130,
          image: 'assets/ui/decoration/icons/living_icon.png',
        };
      case 'toilet':
        return {
          width: 73,
          height: 146,
          image: 'assets/ui/decoration/icons/toilet_icon.png',
        };
      case 'bathroom':
        return {
          width: 145,
          height: 125,
          image: 'assets/ui/decoration/icons/bathroom_icon.png',
        };
      case 'dining':
        return {
          width: 160,
          height: 133,
          image: 'assets/ui/decoration/icons/dining_icon.png',
        };
    }
  }

  /**
   * Show tutorial hand
   */
  private tutorialShow() {
    const { decoration } = StateObserver.getState();

    if (
      isDecorationTutorialInProgress() &&
      decoration.tutorialDecorationScreenWasOpened === false &&
      decoration.tutorialDialogWasShown === true
    ) {
      this.box.addSubview(this.hand);
      this.hand.fadeIn(150, 270, false);
    }
  }

  /**
   * Hide tutorial hand
   */
  private tutorialHide() {
    if (this.hand.getSuperview()) {
      this.hand.fadeOut();
      this.box.removeSubview(this.hand);
    }
  }
}
